'use client'

import { isEmpty, isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

import { useMapStore, ZoomIn } from '@/entities/map'
import { NoSearchResult } from '@/entities/room'
import { cn, useScroll } from '@/shared/lib'
import { Spinner } from '@/shared/ui'

import { useRooms } from '../../model/rooms'
import { RoomItem } from '../room-item'

const ZoomInMessage = () => {
  const t = useTranslations('map')

  return (
    <div className="flex h-[calc(100vh-170px)] w-[545px] shrink-0 flex-col items-center justify-center gap-4">
      <ZoomIn />
      <h3 className="typo-headline2 text-center font-bold text-black">
        {t('list.zoom_message')}
      </h3>
    </div>
  )
}

const NoResultMessage = () => {
  const t = useTranslations('map')

  return (
    <div className="flex h-[calc(100vh-170px)] w-[545px] shrink-0 flex-col items-center justify-center gap-4">
      <NoSearchResult />
      <h3 className="typo-headline2 text-center font-bold whitespace-break-spaces text-black">
        {t('list.no_result.title')}
      </h3>
      <p className="typo-body1 text-center whitespace-break-spaces text-gray-900">
        {t('list.no_result.description')}
      </p>
    </div>
  )
}

type InnerRoomListProps = {
  isMounted: boolean
  scrollToTop: () => void
}

const InnerRoomList = ({ isMounted, scrollToTop }: InnerRoomListProps) => {
  const { rooms, ref, loading, isFirstPage } = useRooms({ isMounted })

  useEffect(() => {
    if (isMounted && isFirstPage) {
      scrollToTop()
    }
  }, [rooms])

  if (!isMounted || loading) return <Spinner />
  if (isNil(rooms)) return <ZoomInMessage />
  if (isEmpty(rooms)) return <NoResultMessage />

  return rooms.map((room, index) => {
    const isLastItem = index === rooms.length - 1

    return (
      <li key={room.rid} className="h-fit" ref={isLastItem ? ref : null}>
        <RoomItem room={room} />
      </li>
    )
  })
}

type RoomListProps = {
  isMounted: boolean
}

export const RoomList = ({ isMounted }: RoomListProps) => {
  const { ref: listRef, scroll } = useScroll<HTMLUListElement>()

  const canFetchRoom = useMapStore(state => state.canFetchRoom)

  return (
    <ul
      className={cn(
        'relative grid h-[calc(100vh-122px)] w-[588px] shrink-0 grid-cols-2 flex-wrap content-start gap-5 border-x border-gray-200 p-5',
        'scrollbar thick overflow-y-auto',
      )}
      ref={listRef}
    >
      {(() => {
        if (isMounted && !canFetchRoom()) return <ZoomInMessage />

        return <InnerRoomList isMounted={isMounted} scrollToTop={scroll} />
      })()}
    </ul>
  )
}
